import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { RefObject } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { TooltipProvider } from "@nexus/ui"
import { CreativeCard } from "../CreativeCard/CreativeCard"
import type { CreativeExample } from "../../../types/opportunities.types"

// Matches the grid's Tailwind breakpoint: `grid-cols-2 sm:grid-cols-4`.
// `sm` is a viewport media query (640px), so matching on window width mirrors
// exactly what the static grid would render.
const SM_BREAKPOINT_PX = 640
const COLUMNS_BASE = 2
const COLUMNS_SM = 4

// Row height is refined per-row by measureElement; this estimate only seeds the
// initial scrollbar size. A card is an aspect-[4/5] thumbnail + caption + the
// gap-4 (16px) row spacing carried as bottom padding on each row.
const ESTIMATED_ROW_PX = 340
const ROW_OVERSCAN = 3

/**
 * Tracks the active column count, mirroring the grid's `sm:grid-cols-4`
 * breakpoint. Returns 2 below 640px, 4 at or above.
 */
function useColumnCount(): number {
  const [cols, setCols] = useState(() =>
    typeof window !== "undefined" && window.innerWidth >= SM_BREAKPOINT_PX
      ? COLUMNS_SM
      : COLUMNS_BASE,
  )

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia(`(min-width: ${SM_BREAKPOINT_PX}px)`)
    const update = () => setCols(mq.matches ? COLUMNS_SM : COLUMNS_BASE)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  return cols
}

export interface CreativeCardContext {
  opportunityId?: string
  opportunitySetId?: string
  opportunityTitle?: string
  segment1?: string | null
  segment2?: string | null
  segment3?: string | null
  feature?: string | null
  subFeature?: string | null
  dataset?: string | null
  engine?: string | null
  industry?: string | null
}

interface VirtualizedCreativeGridProps extends CreativeCardContext {
  items: CreativeExample[]
  emptyLabel: string
  /**
   * The drawer's scroll container. When provided the grid virtualizes against
   * it; when absent (Storybook, isolated tests) it falls back to a plain grid
   * so off-context usage stays unchanged.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>
}

function PlainGrid({ items, cardContext }: { items: CreativeExample[]; cardContext: CreativeCardContext }) {
  return (
    <TooltipProvider delayDuration={300}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {items.map((c) => (
          <CreativeCard key={c.id} creative={c} cardContext={cardContext} />
        ))}
      </div>
    </TooltipProvider>
  )
}

/**
 * Row-virtualized creatives grid. Only the rows within the viewport (plus a
 * small overscan) are mounted, so the number of live <img>/<video> elements —
 * and the decoded image/video bitmaps Chrome retains for them — stays bounded
 * regardless of how many pages have been loaded into the infinite list. This is
 * the primary fix for the unbounded memory growth seen when scrolling long
 * creative grids (AD-1569): off-screen cards are unmounted, not just hidden.
 */
export function VirtualizedCreativeGrid({
  items,
  emptyLabel,
  scrollContainerRef,
  opportunityId,
  opportunitySetId,
  opportunityTitle,
  segment1,
  segment2,
  segment3,
  feature,
  subFeature,
  dataset,
  engine,
  industry,
}: VirtualizedCreativeGridProps) {
  const cardContext: CreativeCardContext = { opportunityId, opportunitySetId, opportunityTitle, segment1, segment2, segment3, feature, subFeature, dataset, engine, industry }
  const cols = useColumnCount()
  const gridRef = useRef<HTMLDivElement>(null)
  const rowCount = Math.ceil(items.length / cols)

  // The grid sits below the metrics/findings sections inside the shared drawer
  // scroll container, so the virtualizer needs to know that offset to map
  // scroll position onto the correct rows. Measure the grid's top relative to
  // the scroll container and re-measure when either resizes.
  const [scrollMargin, setScrollMargin] = useState(0)
  useLayoutEffect(() => {
    const grid = gridRef.current
    const scroller = scrollContainerRef?.current
    if (!grid || !scroller) return

    const measure = () => {
      const offset =
        grid.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop
      setScrollMargin(offset)
    }
    measure()

    const ro = new ResizeObserver(measure)
    ro.observe(scroller)
    ro.observe(grid)
    return () => ro.disconnect()
  }, [scrollContainerRef, items.length, cols])

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollContainerRef?.current ?? null,
    estimateSize: () => ESTIMATED_ROW_PX,
    overscan: ROW_OVERSCAN,
    scrollMargin,
  })

  // When the grid mounts in the same commit as a freshly-created scroll
  // container (e.g. the drilldown page, which renders its own scroller only
  // once data has loaded), the virtualizer can latch a 0×0 viewport rect before
  // flex layout has resolved the container's real height. With outerSize 0 it
  // computes an empty range and renders no rows — the grid looks empty until a
  // scroll event forces a re-measure. Re-measure once after layout settles so
  // the real viewport size is picked up. Gate on the ref object itself (not
  // `.current`): only the virtualized path receives one, so the plain-grid
  // fallback schedules nothing, while the real page still re-measures even
  // though `.current` is briefly null here (a parent's ref is attached after
  // this child's layout effect runs — it's populated by the time the rAF runs).
  useLayoutEffect(() => {
    if (!scrollContainerRef) return
    const id = requestAnimationFrame(() => virtualizer.measure())
    return () => cancelAnimationFrame(id)
  }, [virtualizer, scrollContainerRef])

  if (items.length === 0) {
    return <p className="py-6 text-center text-xs text-muted-foreground">{emptyLabel}</p>
  }

  // No scroll container in context (stories / isolated tests): render everything.
  if (!scrollContainerRef) {
    return <PlainGrid items={items} cardContext={cardContext} />
  }

  const virtualRows = virtualizer.getVirtualItems()

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={gridRef}
        style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}
      >
        {virtualRows.map((virtualRow) => {
          const start = virtualRow.index * cols
          const rowItems = items.slice(start, start + cols)
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
              }}
            >
              {/* pb-4 carries the gap-4 row spacing into the measured row height */}
              <div className="grid grid-cols-2 gap-4 pb-4 sm:grid-cols-4">
                {rowItems.map((c) => (
                  <CreativeCard key={c.id} creative={c} cardContext={cardContext} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </TooltipProvider>
  )
}
